/**
 * Kundenformular — Formularzustand und Umrechnungen, geteilt von der
 * Kundenseite und dem Kunden-Blatt auf der Aufgabenseite (22.09.2026 aus
 * KundenPage.tsx herausgelöst, Verhalten unverändert).
 */
import type { Kunde } from "./types";
import { ANREDEN, namenTeilen, type Anrede } from "./anrede";
import { leererKontakt, parseKontakte, type KontaktZeile } from "./kundeAnlage";

// ── Typen (Parität Desktop-CRM) ───────────────────────────────────────────
export type Kundentyp = "privat" | "firma";
export type Kommunikationskanal = "" | "telefon" | "mail" | "whatsapp" | "telegram";

export type KForm = {
  kundentyp: Kundentyp;
  name: string;
  email: string;
  phone: string;
  kommunikationskanal: Kommunikationskanal;
  website: string;
  strasse: string;
  plz: string;
  ort: string;
  rechnung_strasse: string;
  rechnung_plz: string;
  rechnung_ort: string;
  notes: string;
  anrede: Anrede;
  vorname: string;
  nachname: string;
  kontakte: KontaktZeile[];
  pflege_monate: Record<string, string>;
};

export const MONATE: readonly [number, string, string][] = [
  [1, "Januar", "Jan"], [2, "Februar", "Feb"], [3, "März", "Mär"], [4, "April", "Apr"],
  [5, "Mai", "Mai"], [6, "Juni", "Jun"], [7, "Juli", "Jul"], [8, "August", "Aug"],
  [9, "September", "Sep"], [10, "Oktober", "Okt"], [11, "November", "Nov"], [12, "Dezember", "Dez"],
];

export function emptyForm(): KForm {
  return {
    kundentyp: "firma", name: "", email: "", phone: "", kommunikationskanal: "",
    website: "", strasse: "", plz: "", ort: "",
    rechnung_strasse: "", rechnung_plz: "", rechnung_ort: "",
    notes: "", anrede: "", vorname: "", nachname: "", kontakte: [], pflege_monate: {},
  };
}

export function emptyKontakt(): KontaktZeile {
  return leererKontakt();
}

/** Wie DB/Desktop: zwei Zeilen „Straße“ + „PLZ Ort“. */
export function denormAddr(strasse: string, plz: string, ort: string): string {
  const s = (strasse || "").trim();
  const rest = `${(plz || "").trim()} ${(ort || "").trim()}`.trim();
  if (s && rest) return `${s}\n${rest}`;
  return s || rest;
}

/**
 * Gegenstück zu denormAddr: zerlegt eine Freitext-Anschrift wieder in die
 * Einzelfelder.
 *
 * Notwendig, weil 16 Kunden ihre Anschrift ausschliesslich in `lieferadresse`
 * tragen — Altbestand, den Migration 076 nicht zerlegen konnte. Die Liste zeigte
 * sie ueber den Fallback an, das Formular las nur die Einzelfelder, und beim
 * Speichern schrieb `denormAddr` den leeren Stand zurueck: eine Aenderung an der
 * Telefonnummer loeschte die Adresse.
 *
 * Erkannte Formen: zwei Zeilen (Strasse / PLZ Ort), eine Zeile mit Komma
 * („Musterweg 1, 01067 Dresden" oder ohne PLZ „Musterweg 1, Dresden") und die
 * reine Ortszeile („01689 Niederau OT Ockrilla"). Was sich nicht zerlegen laesst,
 * landet vollstaendig in `strasse` — sichtbar und korrigierbar ist besser als weg.
 */
export function normAddr(freitext: string): { strasse: string; plz: string; ort: string } {
  const leer = { strasse: "", plz: "", ort: "" };
  const text = (freitext || "").trim();
  if (!text) return leer;

  const teile = text.includes("\n")
    ? text.split("\n").map((t) => t.trim()).filter(Boolean)
    : text.split(",").map((t) => t.trim()).filter(Boolean);

  const ortsteil = (roh: string): { plz: string; ort: string } | null => {
    const treffer = /^(\d{5})\s+(.+)$/.exec(roh);
    return treffer ? { plz: treffer[1], ort: treffer[2].trim() } : null;
  };

  if (teile.length >= 2) {
    // Die Ortszeile ist der erste Teil mit fuehrender PLZ — nicht zwingend der
    // letzte: bei „Am Steinbruch 22, 01705 Freital, Deutschland" haengt hinten
    // noch das Land. Alles ab der PLZ gehoert zusammen in die Ortszeile.
    const mitPlz = teile.findIndex((t) => ortsteil(t) !== null);
    if (mitPlz > 0) {
      const zerlegt = ortsteil(teile.slice(mitPlz).join(", "));
      if (zerlegt) return { strasse: teile.slice(0, mitPlz).join(", "), ...zerlegt };
    }
    const hinten = teile[teile.length - 1];
    // Ohne PLZ ist der hintere Teil trotzdem der Ort — genau so steht es bei
    // „Eugen-Dietrich-Str. 3, Dresden" in der Datenbank.
    return { strasse: teile.slice(0, -1).join(", "), plz: "", ort: hinten };
  }

  const nurOrt = ortsteil(teile[0] ?? "");
  if (nurOrt) return { strasse: "", ...nurOrt };
  return { strasse: text, plz: "", ort: "" };
}


export function kommFromRaw(raw: string): Kommunikationskanal {
  const v = (raw || "").trim().toLowerCase();
  return v === "telefon" || v === "mail" || v === "whatsapp" || v === "telegram" ? v : "";
}

export function rowToForm(k: Kunde): KForm {
  const kt: Kundentyp = k.kundentyp === "privat" ? "privat" : "firma";
  // Dieselbe Reihenfolge, die displayAnschrift fuer die Liste benutzt. Sonst
  // zeigt die Liste eine Anschrift, die das Formular nicht kennt — und beim
  // Speichern verschwindet sie.
  const liefer = k.strasse || k.plz || k.ort
    ? { strasse: k.strasse || "", plz: k.plz || "", ort: k.ort || "" }
    : normAddr(k.lieferadresse || k.adresse || "");
  const rechnung = k.rechnung_strasse || k.rechnung_plz || k.rechnung_ort
    ? {
        strasse: k.rechnung_strasse || "",
        plz: k.rechnung_plz || "",
        ort: k.rechnung_ort || "",
      }
    : normAddr(k.rechnungsadresse || "");
  return {
    kundentyp: kt,
    name: k.name || "",
    email: k.email || "",
    phone: k.phone || "",
    kommunikationskanal: kommFromRaw(k.kommunikationskanal || ""),
    website: k.website || "",
    strasse: liefer.strasse,
    plz: liefer.plz,
    ort: liefer.ort,
    rechnung_strasse: rechnung.strasse,
    rechnung_plz: rechnung.plz,
    rechnung_ort: rechnung.ort,
    notes: k.notes || "",
    anrede: (ANREDEN as readonly string[]).includes(k.anrede || "") ? (k.anrede as Anrede) : "",
    // Altbestand ohne eigene Felder: aus dem Namen zerlegen (Migration 170).
    ...(k.vorname || k.nachname
      ? { vorname: k.vorname || "", nachname: k.nachname || "" }
      : namenTeilen(k.name || "", k.anrede_name || "")),
    kontakte: parseKontakte(k.kontakte_json || "[]"),
    pflege_monate: { ...(k.pflege_monate || {}) },
  };
}

export function mapsUrl(addr: string): string {
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(addr.replace(/\n/g, ", ").trim())}`;
}
